import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ThemeToggle from './components/ThemeToggle';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsent from './components/CookieConsent';
import ArabicLoader from './components/ArabicLoader';
import PWAInstallSheet from './components/PWAInstallSheet';
import UpdateNudge from './components/UpdateNudge';
import { trackEvent } from './lib/analytics';

// Catches Supabase auth codes that land on the wrong page (e.g. /home?code=…)
// when the Supabase SITE_URL is misconfigured. Redirects silently to /auth/callback.
// Skips /auth/callback and /reset-password — they handle codes themselves.
function AuthCodeInterceptor() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (pathname === '/auth/callback' || pathname === '/reset-password') return;
    if (search.includes('code=') || search.includes('error=')) {
      navigate(`/auth/callback${search}`, { replace: true });
    }
  }, [pathname, search, navigate]);
  return null;
}

// Forwards auth tokens that land at the root to /auth/callback.
// Covers two cases:
//   ?code=  — PKCE flow (Supabase SITE_URL misconfigured to point at root)
//   #access_token= — implicit/hybrid flow where Supabase delivers token in hash
function RootRedirect() {
  const { search, hash } = useLocation();
  if (search.includes('code=') || search.includes('error=')) {
    return <Navigate to={`/auth/callback${search}`} replace />;
  }
  if (hash.includes('access_token=') || hash.includes('error_description=')) {
    return <Navigate to={`/auth/callback${hash}`} replace />;
  }
  return <Navigate to="/splash" replace />;
}

const Splash        = lazy(() => import('./screens/Splash'));
const AuthCallback  = lazy(() => import('./screens/AuthCallback'));
const ResetPassword = lazy(() => import('./screens/ResetPassword'));
const Onboard       = lazy(() => import('./screens/Onboard'));
const Home          = lazy(() => import('./screens/Home'));
const Poem          = lazy(() => import('./screens/Poem'));
const Words         = lazy(() => import('./screens/Words'));
const Review        = lazy(() => import('./screens/Review'));
const Feedback      = lazy(() => import('./screens/Feedback'));
const Saved         = lazy(() => import('./screens/Saved'));
const AdminFeedback = lazy(() => import('./screens/AdminFeedback'));
const AllPaths      = lazy(() => import('./screens/AllPaths'));
const Search        = lazy(() => import('./screens/Search'));
const ThemeDetail    = lazy(() => import('./screens/ThemeDetail'));
const Achievements   = lazy(() => import('./screens/Achievements'));
const PricingPage      = lazy(() => import('./pages/PricingPage'));
const CheckoutSuccess  = lazy(() => import('./pages/CheckoutSuccess'));
const PrivacyPage      = lazy(() => import('./pages/Privacy'));

// Wraps ErrorBoundary with the current pathname so the error state resets
// automatically whenever the user navigates to a different route.
function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

// Onboarding flow
const OnboardingWelcome       = lazy(() => import('./app/onboarding/screens/Welcome'));
const OnboardingName          = lazy(() => import('./app/onboarding/screens/Q0_Name'));
const OnboardingTrackSelect   = lazy(() => import('./app/onboarding/screens/TrackSelect'));
const OnboardingQ1            = lazy(() => import('./app/onboarding/screens/Q1_Reading'));
const OnboardingQ2            = lazy(() => import('./app/onboarding/screens/Q2_Comprehension'));
const OnboardingQ3            = lazy(() => import('./app/onboarding/screens/Q3_Goals'));
const OnboardingQ4            = lazy(() => import('./app/onboarding/screens/Q4_Pace'));
const OnboardingQ5            = lazy(() => import('./app/onboarding/screens/Q5_When'));
const OnboardingSummary       = lazy(() => import('./app/onboarding/screens/Summary'));

export default function App() {
  const { pathname } = useLocation();

  useEffect(() => {
    function onInstalled() { trackEvent('pwa_installed'); }
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  return (
    <>
      {/* Home manages its own inline toggle to avoid clashing with S and ? buttons */}
      {pathname !== '/home' && <ThemeToggle />}
      <CookieConsent />
      <PWAInstallSheet />
      <UpdateNudge />
      <RouteErrorBoundary>
        <Suspense fallback={
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100svh', background: 'var(--bg)' }}>
            <ArabicLoader message="Opening the Dīwān…" />
          </div>
        }>
        <AuthCodeInterceptor />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/splash" element={<Splash />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/onboard" element={<Onboard />} />
          <Route path="/onboarding"         element={<OnboardingWelcome />} />
          <Route path="/onboarding/name"    element={<OnboardingName />} />
          <Route path="/onboarding/track"   element={<OnboardingTrackSelect />} />
          <Route path="/onboarding/q1"      element={<OnboardingQ1 />} />
          <Route path="/onboarding/q2"      element={<OnboardingQ2 />} />
          <Route path="/onboarding/q3"      element={<OnboardingQ3 />} />
          <Route path="/onboarding/q4"      element={<OnboardingQ4 />} />
          <Route path="/onboarding/q5"      element={<OnboardingQ5 />} />
          <Route path="/onboarding/summary" element={<OnboardingSummary />} />
          <Route path="/home" element={<Home />} />
          <Route path="/poem/:poemId" element={<Poem />} />
          <Route path="/words" element={<Words />} />
          <Route path="/achievements" element={<Achievements />} />
          <Route path="/review" element={<Review />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/saved" element={<Saved />} />
          <Route path="/admin/feedback" element={<AdminFeedback />} />
          <Route path="/search" element={<Search />} />
          <Route path="/all-paths" element={<AllPaths />} />
          <Route path="/theme/:themeId" element={<ThemeDetail />} />
          <Route path="/premium" element={<PricingPage />} />
          <Route path="/welcome" element={<CheckoutSuccess />} />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </>
  );
}
